import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb, { type BreadcrumbItem } from '@/components/base/Breadcrumb';

export default function AboutPage() {
  const { t } = useTranslation();
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    document.title = '关于我们 - 韩影精选';
  }, []);

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: t('breadcrumb.home'), href: '/' },
    { label: t('nav.about') },
  ];

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('sending');
    setFormError('');

    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9qr7sme3oq4jqumtpg0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormError(serverMsg || t('form.error'));
        setFormStatus('error');
      }
    } catch {
      setFormError(t('form.error'));
      setFormStatus('error');
    }
  };

  const orgLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩影精选',
    url: 'https://korean-film-review.readdy.ai/',
    description: '韩国伦理电影深度解析与推荐平台',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  const aboutLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于韩影精选',
    url: 'https://korean-film-review.readdy.ai/about',
  };

  const stats = [
    { value: '200+', label: '深度影评' },
    { value: '50+', label: '合作影评人' },
    { value: '120万+', label: '月均阅读量' },
    { value: '98%', label: '读者好评率' },
  ];

  const testimonials = [
    {
      name: '李明浩',
      role: '电影研究者',
      content: '韩影精选是我研究韩国电影最重要的参考来源之一，每一篇影评的深度和专业性都令人钦佩。',
    },
    {
      name: '金秀雅',
      role: '电影专业学生',
      content: '从影评写作的角度来看，韩影精选的文章结构清晰、观点独到，对我的论文写作帮助很大。',
    },
    {
      name: '王子轩',
      role: '资深影迷',
      content: '关注韩影精选三年了，每一篇推送都不会错过。这里的影评不是简单的剧情复述，而是真正有深度的分析。',
    },
  ];

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(orgLd)}</script>
      <script type="application/ld+json">{JSON.stringify(aboutLd)}</script>

      <Navbar />

      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-8">
            {t('about.title')}
          </h1>

          <section className="mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <span className="inline-block px-4 py-1 bg-primary-100 text-primary-700 text-xs font-semibold rounded-full uppercase tracking-wider mb-4">
                  About
                </span>
                <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-900 mb-6">
                  {t('about.brand_intro')}
                </h2>
                <div className="space-y-4 text-foreground-700 leading-relaxed">
                  <p>
                    韩影精选成立于2020年，是国内首批专注于韩国伦理电影研究的深度内容平台。我们相信，韩国伦理电影不仅仅是娱乐产品，更是韩国社会的一面镜子——从禁忌之恋到阶级冲突，从历史反思到性别议题，每一部优秀的韩国伦理电影都在以其独特的方式回应着时代的叩问。
                  </p>
                  <p>
                    我们的编辑团队由电影学研究者、资深影评人和文化评论者组成，致力于为读者提供兼具学术深度和阅读趣味的高质量影评内容。每一篇文章都经过严格的事实核查和多轮编辑审校，确保信息的准确性和观点的独立性。
                  </p>
                  <p>
                    韩影精选的使命是：让更多人看到韩国伦理电影的艺术价值，超越刻板印象，发现光影背后的人性光芒。
                  </p>
                </div>
              </div>
              <div className="rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Elegant%20modern%20home%20cinema%20room%20with%20a%20large%20projector%20screen%20displaying%20a%20classic%20film%20scene%2C%20comfortable%20velvet%20armchairs%2C%20warm%20ambient%20lighting%20from%20wall%20sconces%2C%20shelves%20filled%20with%20film%20books%20and%20posters%2C%20sophisticated%20interior%20design%2C%20cozy%20atmosphere%2C%20warm%20amber%20and%20deep%20burgundy%20tones&width=800&height=600&seq=about-cinema-2024&orientation=landscape"
                  alt="韩影精选品牌形象"
                  title="韩影精选品牌形象"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </section>

          <section className="mb-16 bg-background-100 rounded-2xl py-12 px-6 md:px-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              {stats.map((stat) => (
                <div key={stat.label}>
                  <div className="font-heading text-3xl md:text-4xl font-bold text-primary-600 mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm text-foreground-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <span className="inline-block px-4 py-1 bg-accent-100 text-accent-800 text-xs font-semibold rounded-full uppercase tracking-wider mb-4">
              Reputation
            </span>
            <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-900 mb-10">
              {t('about.reputation')}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((item) => (
                <div key={item.name} className="bg-background-50 border border-background-200/70 rounded-lg p-6">
                  <div className="flex items-center gap-1 mb-4">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i key={star} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    &ldquo;{item.content}&rdquo;
                  </p>
                  <div>
                    <p className="font-semibold text-foreground-900 text-sm">{item.name}</p>
                    <p className="text-xs text-foreground-500">{item.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <span className="inline-block px-4 py-1 bg-secondary-100 text-secondary-800 text-xs font-semibold rounded-full uppercase tracking-wider mb-4">
              Contact
            </span>
            <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-900 mb-2">
              {t('about.contact')}
            </h2>
            <p className="text-foreground-600 mb-8">{t('about.contact_desc')}</p>

            <div className="max-w-2xl">
              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="space-y-5"
              >
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-800 mb-2">
                    {t('form.name')}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-3 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                    placeholder="请输入您的姓名"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-2">
                    {t('form.email')}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                    placeholder="请输入您的邮箱"
                  />
                </div>

                <div className="sp-protect">
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-800 mb-2">
                    {t('form.message')}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                    placeholder="请输入您想说的话（500字以内）"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={formStatus === 'sending'}
                  className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-background-50 font-medium rounded-full hover:bg-primary-600 transition-all disabled:opacity-60 whitespace-nowrap cursor-pointer"
                >
                  {formStatus === 'sending' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      <span>{t('form.sending')}</span>
                    </>
                  ) : (
                    <span>{t('form.submit')}</span>
                  )}
                </button>

                {formStatus === 'success' && (
                  <p className="text-sm text-green-600 bg-green-50 px-4 py-3 rounded-lg">
                    {t('form.success')}
                  </p>
                )}

                {formStatus === 'error' && formError && (
                  <p className="text-sm text-red-600 bg-red-50 px-4 py-3 rounded-lg">
                    {formError}
                  </p>
                )}
              </form>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}